import React, { useMemo } from 'react'
import { Sun, LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useGlobalTasks, Tab } from '../../../hooks/multiWindow'
import { DormantIcon, DormantOpenIcon } from '../../../components/icons/DormantIcon'
import { UpcomingIcon, UpcomingOpenIcon } from '../../../components/icons/UpcomingIcon'
import { GoalsIcon } from '../../../components/icons/GoalsIcon'
import { QuickNotesFilledIcon, QuickNotesEmptyIcon, QuickNotesFilledOpenIcon, QuickNotesEmptyOpenIcon } from '../../../components/icons'
import { useQuickNotesHasEntries } from '../../../hooks/useQuickNotesHasEntries'
import { RecurringIcon } from '../../../components/icons/RecurringIcon'
import { SproutIcon } from '../../../components/icons/SproutIcon'
import { SidebarSection } from './SidebarSection'
import { SidebarItem } from './SidebarItem'
import { ProjectsSection } from './ProjectsSection'

interface HomeSidebarContentProps {
  activeTab?: Tab
  openTab: (tab: Tab) => void
  navigateActiveTab: (updates: { path: string; title: string; icon?: LucideIcon }) => void
  tabs?: Tab[]
  onCloseTabByPath?: (path: string) => void
  onUnpinByPath?: (path: string) => void
  onPinByPath?: (path: string) => void
  openPreviewTab?: (tab: Omit<Tab, 'id'> & { id?: string }) => void
  promotePreviewBySpace?: (lifeSpace: string) => void
}

export const HomeSidebarContent: React.FC<HomeSidebarContentProps> = ({ activeTab, openTab, navigateActiveTab, tabs = [], onCloseTabByPath, onUnpinByPath, onPinByPath, openPreviewTab, promotePreviewBySpace }) => {
  const { t } = useTranslation()
  const { tasks } = useGlobalTasks()
  const quickNotesHasEntries = useQuickNotesHasEntries('home')

  // Path-indexed sets derived from `tabs`. Memoized so sidebar re-renders
  // don't rebuild Sets on every tab switch.
  const { openPaths, previewPaths, pinnedPaths } = useMemo(() => {
    const open = new Set<string>()
    const preview = new Set<string>()
    const pinned = new Set<string>()
    const hasUnpinned = new Set<string>()
    for (const tab of tabs) {
      open.add(tab.path)
      if (tab.isPreview) preview.add(tab.path)
      if (tab.isPinned) pinned.add(tab.path)
      else hasUnpinned.add(tab.path)
    }
    for (const path of hasUnpinned) pinned.delete(path)
    return { openPaths: open, previewPaths: preview, pinnedPaths: pinned }
  }, [tabs])
  const quickNotesTabOpen = openPaths.has('/home/home/quick-notes')
  const QuickNotesIcon = quickNotesHasEntries
    ? (quickNotesTabOpen ? QuickNotesFilledOpenIcon : QuickNotesFilledIcon)
    : (quickNotesTabOpen ? QuickNotesEmptyOpenIcon : QuickNotesEmptyIcon)
  // Tabs carry the inactive (currentColor) variant; DraggableTab swaps it to
  // the violet "open" variant for the currently active tab via reference map.
  const QuickNotesTabIcon = quickNotesHasEntries ? QuickNotesFilledIcon : QuickNotesEmptyIcon
  const DormantSidebarIcon = openPaths.has('/home/home/dormant') ? DormantOpenIcon : DormantIcon
  const UpcomingSidebarIcon = openPaths.has('/home/home/upcoming') ? UpcomingOpenIcon : UpcomingIcon

  // Ruhende Aufgaben (pausedAt != null) werden in Demnächst nicht
  // mitgezählt; sie erscheinen nur in Ruhend. Memoized — `tasks` is the
  // only input, so the count survives tab-switch re-renders.
  const upcomingCount = useMemo(() => {
    const todayMs = new Date().setHours(0, 0, 0, 0)
    let count = 0
    for (const t of tasks) {
      if (t.pausedAt != null) continue
      if (t.lifeSpace !== 'home') continue
      if (!t.dueDate) continue
      const dayDiff = Math.floor((new Date(t.dueDate).getTime() - todayMs) / 86_400_000)
      if (dayDiff > 0 && dayDiff <= 7) count++
    }
    return count
  }, [tasks])

  const handleOpenTab = (e: React.MouseEvent, id: string, path: string, title: string, icon?: LucideIcon) => {
    if (e.metaKey || e.ctrlKey) {
      openTab({
        id: `${id}-${Date.now()}`,
        path,
        title,
        icon,
        lifeSpace: 'home',
        isDirty: false,
        isPinned: false
      })
    } else if (openPreviewTab) {
      openPreviewTab({ path, title, icon, lifeSpace: 'home' })
    } else {
      navigateActiveTab({ path, title, icon })
    }
  }

  const handlePromote = promotePreviewBySpace ? () => promotePreviewBySpace('home') : undefined

  const handleDoubleClick = (path: string) => {
    if (previewPaths.has(path)) {
      promotePreviewBySpace?.('home')
    } else if (!openPaths.has(path)) {
      promotePreviewBySpace?.('home')
    }
  }

  return (
    <>
      <div className="standard-sidebar__quick-notes">
        <SidebarItem
          icon={QuickNotesIcon as unknown as LucideIcon}
          label={t('sidebar.quickNotes')}
          path="/home/home/quick-notes"
          isActive={activeTab?.path === '/home/home/quick-notes'}
          hasOpenTab={openPaths.has('/home/home/quick-notes')}
          isPreview={previewPaths.has('/home/home/quick-notes')}
          isPinned={pinnedPaths.has('/home/home/quick-notes')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/home/quick-notes') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/home/quick-notes') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/home/quick-notes') : undefined}
          onOpenNewTab={() => openTab({ id: `home-quick-notes-${Date.now()}`, path: '/home/home/quick-notes', title: t('sidebar.quickNotes'), icon: QuickNotesTabIcon as unknown as LucideIcon, lifeSpace: 'home', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'home-quick-notes', '/home/home/quick-notes', t('sidebar.quickNotes'), QuickNotesTabIcon as unknown as LucideIcon)}
          onDoubleClick={() => handleDoubleClick('/home/home/quick-notes')}
          onPromote={handlePromote}
        />
      </div>
      <SidebarSection
        title={t('sidebar.homeSection')}
        defaultExpanded
      >
        <SidebarItem
          icon={Sun}
          label={t('sidebar.today')}
          path="/home/home/today"
          isActive={activeTab?.path === '/home/home/today'}
          hasOpenTab={openPaths.has('/home/home/today')}
          isPreview={previewPaths.has('/home/home/today')}
          isPinned={pinnedPaths.has('/home/home/today')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/home/today') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/home/today') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/home/today') : undefined}
          onOpenNewTab={() => openTab({ id: `home-today-${Date.now()}`, path: '/home/home/today', title: t('sidebar.today'), icon: Sun, lifeSpace: 'home', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'home-today', '/home/home/today', t('sidebar.today'), Sun)}
          onDoubleClick={() => handleDoubleClick('/home/home/today')}
          onPromote={handlePromote}
        />
        <SidebarItem
          icon={UpcomingSidebarIcon as unknown as LucideIcon}
          label={t('sidebar.upcoming')}
          badge={upcomingCount}
          path="/home/home/upcoming"
          isActive={activeTab?.path === '/home/home/upcoming'}
          hasOpenTab={openPaths.has('/home/home/upcoming')}
          isPreview={previewPaths.has('/home/home/upcoming')}
          isPinned={pinnedPaths.has('/home/home/upcoming')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/home/upcoming') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/home/upcoming') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/home/upcoming') : undefined}
          onOpenNewTab={() => openTab({ id: `home-upcoming-${Date.now()}`, path: '/home/home/upcoming', title: t('sidebar.upcoming'), icon: UpcomingIcon as unknown as LucideIcon, lifeSpace: 'home', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'home-upcoming', '/home/home/upcoming', t('sidebar.upcoming'), UpcomingIcon as unknown as LucideIcon)}
          onDoubleClick={() => handleDoubleClick('/home/home/upcoming')}
          onPromote={handlePromote}
        />
        <SidebarItem
          icon={DormantSidebarIcon as LucideIcon}
          label={t('sidebar.dormant')}
          path="/home/home/dormant"
          isActive={activeTab?.path === '/home/home/dormant'}
          hasOpenTab={openPaths.has('/home/home/dormant')}
          isPreview={previewPaths.has('/home/home/dormant')}
          isPinned={pinnedPaths.has('/home/home/dormant')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/home/dormant') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/home/dormant') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/home/dormant') : undefined}
          onOpenNewTab={() => openTab({ id: `home-dormant-${Date.now()}`, path: '/home/home/dormant', title: t('sidebar.dormant'), icon: DormantIcon as LucideIcon, lifeSpace: 'home', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'home-dormant', '/home/home/dormant', t('sidebar.dormant'), DormantIcon as LucideIcon)}
          onDoubleClick={() => handleDoubleClick('/home/home/dormant')}
          onPromote={handlePromote}
        />
      </SidebarSection>

      <SidebarSection
        title={t('sidebar.homeSpaceSection')}
        defaultExpanded
      >
        <SidebarItem
          icon={SproutIcon as unknown as LucideIcon}
          label={t('sidebar.habits')}
          path="/home/home/habits"
          isActive={activeTab?.path === '/home/home/habits'}
          hasOpenTab={openPaths.has('/home/home/habits')}
          isPreview={previewPaths.has('/home/home/habits')}
          isPinned={pinnedPaths.has('/home/home/habits')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/home/habits') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/home/habits') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/home/habits') : undefined}
          onOpenNewTab={() => openTab({ id: `home-habits-${Date.now()}`, path: '/home/home/habits', title: t('sidebar.habits'), icon: SproutIcon as unknown as LucideIcon, lifeSpace: 'home', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'home-habits', '/home/home/habits', t('sidebar.habits'), SproutIcon as unknown as LucideIcon)}
          onDoubleClick={() => handleDoubleClick('/home/home/habits')}
          onPromote={handlePromote}
        />
        <SidebarItem
          icon={RecurringIcon as unknown as LucideIcon}
          label={t('sidebar.recurring')}
          path="/home/home/recurring"
          isActive={activeTab?.path === '/home/home/recurring'}
          hasOpenTab={openPaths.has('/home/home/recurring')}
          isPreview={previewPaths.has('/home/home/recurring')}
          isPinned={pinnedPaths.has('/home/home/recurring')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/home/recurring') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/home/recurring') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/home/recurring') : undefined}
          onOpenNewTab={() => openTab({ id: `home-recurring-${Date.now()}`, path: '/home/home/recurring', title: t('sidebar.recurring'), icon: RecurringIcon as unknown as LucideIcon, lifeSpace: 'home', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'home-recurring', '/home/home/recurring', t('sidebar.recurring'), RecurringIcon as unknown as LucideIcon)}
          onDoubleClick={() => handleDoubleClick('/home/home/recurring')}
          onPromote={handlePromote}
        />
        <SidebarItem
          icon={GoalsIcon}
          label={t('sidebar.goals')}
          path="/home/home/goals"
          isActive={activeTab?.path === '/home/home/goals'}
          hasOpenTab={openPaths.has('/home/home/goals')}
          isPreview={previewPaths.has('/home/home/goals')}
          isPinned={pinnedPaths.has('/home/home/goals')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/home/goals') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/home/goals') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/home/goals') : undefined}
          onOpenNewTab={() => openTab({ id: `home-goals-${Date.now()}`, path: '/home/home/goals', title: t('sidebar.goals'), icon: GoalsIcon as unknown as LucideIcon, lifeSpace: 'home', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'home-goals', '/home/home/goals', t('sidebar.goals'), GoalsIcon as unknown as LucideIcon)}
          onDoubleClick={() => handleDoubleClick('/home/home/goals')}
          onPromote={handlePromote}
        />
      </SidebarSection>

      <ProjectsSection
        lifeSpace="home"
        activeTab={activeTab}
        tabs={tabs}
        openTab={openTab}
        navigateActiveTab={navigateActiveTab}
        onCloseTabByPath={onCloseTabByPath}
        onUnpinByPath={onUnpinByPath}
        onPinByPath={onPinByPath}
        openPaths={openPaths}
        previewPaths={previewPaths}
        pinnedPaths={pinnedPaths}
        onOpenTab={handleOpenTab}
        onDoubleClick={handleDoubleClick}
        onPromote={handlePromote}
        showTutorial
      />
    </>
  )
}
